import { FC, useMemo, useState } from "react";
import { useGummiwormContext } from "@/context/GummiwormContext";
import { TOKEN_LORE } from "@/lib/lore";
import type { NewsItem } from "@/lib/wsTypes";
import { buildTokenLinkIndex, linkifyHeadline } from "@/lib/newsLinks";

/**
 * Dedicated competition news page: every published headline, newest first,
 * grouped by day, filterable by token. The wire reports events and nothing
 * more — it never states direction or severity; reading that out of the
 * prose is the game. Token names in a headline link to that token's market.
 */

type TokenLinkIndex = ReturnType<typeof buildTokenLinkIndex>;

function dayLabel(ms: bigint): string {
  const d = new Date(Number(ms));
  const today = new Date();
  const yesterday = new Date(Date.now() - 86_400_000);
  const same = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (same(d, today)) return "Today";
  if (same(d, yesterday)) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
}

function timeLabel(ms: bigint): string {
  return new Date(Number(ms)).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

const NewsRow: FC<{ item: NewsItem; index: TokenLinkIndex; onTradeMarket: (marketKey: string) => void }> = ({
  item,
  index,
  onTradeMarket,
}) => (
  <div className="flex items-start gap-3 border-b border-sr-border-subtle px-4 py-3 last:border-b-0">
    <span className="w-14 shrink-0 pt-0.5 text-xs tabular-nums text-sr-neutral-500">{timeLabel(item.timeMs)}</span>
    <div className="min-w-0 flex-1">
      <p className="text-sm text-sr-neutral-100">{linkifyHeadline(item.headline, index, onTradeMarket)}</p>
      <div className="mt-1 flex flex-wrap items-center gap-1.5">
        <span className="rounded bg-sr-surface px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sr-neutral-300">
          {item.token}
        </span>
        {item.announced && (
          <span className="rounded bg-sr-lemon-muted/15 px-1.5 py-0.5 text-[10px] font-medium text-sr-lemon-muted">
            scheduled
          </span>
        )}
      </div>
    </div>
  </div>
);

export const NewsPage: FC<{ onTradeMarket: (marketKey: string) => void }> = ({ onTradeMarket }) => {
  const { ledgerView, markets } = useGummiwormContext();
  const news = (ledgerView?.news ?? []) as NewsItem[];
  const [token, setToken] = useState<string | null>(null);
  const linkIndex = useMemo(() => buildTokenLinkIndex(markets), [markets]);

  const tokens = useMemo(() => [...new Set(news.map((n) => n.token))].sort(), [news]);

  const groups = useMemo(() => {
    const filtered = news.filter((n) => !token || n.token === token);
    const byDay: { label: string; items: NewsItem[] }[] = [];
    for (let i = filtered.length - 1; i >= 0; i--) {
      const item = filtered[i];
      const label = dayLabel(item.timeMs);
      const last = byDay[byDay.length - 1];
      if (last && last.label === label) last.items.push(item);
      else byDay.push({ label, items: [item] });
    }
    return byDay;
  }, [news, token]);

  const [primerOpen, setPrimerOpen] = useState(false);

  return (
    <div className="w-full max-w-2xl">
      <div className="mb-4 flex items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-white">Newswire</h1>
        </div>
        <button
          onClick={() => setPrimerOpen((o) => !o)}
          className="sr-focus-ring rounded-full bg-sr-surface px-3 py-1 text-xs font-medium text-sr-neutral-300 transition-colors hover:text-white"
          aria-expanded={primerOpen}
        >
          {primerOpen ? "Hide the cast" : "Meet the cast"}
        </button>
      </div>

      {primerOpen && (
        <div className="mb-4 grid gap-1 sm:grid-cols-2">
          {Object.values(TOKEN_LORE).map((l) => (
            <div key={l.token} className="sr-panel px-3 py-3">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-semibold text-white">{l.token}</span>
                <span className="truncate text-xs text-sr-neutral-400">{l.company}</span>
              </div>
              <p className="mt-1 text-xs font-medium text-sr-lemon-muted">{l.motto}</p>
              <p className="mt-1.5 text-xs leading-relaxed text-sr-neutral-300">{l.character}</p>
            </div>
          ))}
        </div>
      )}

      <div className="mb-3 flex flex-wrap gap-1.5">
        <button
          onClick={() => setToken(null)}
          className={`sr-focus-ring rounded-full px-3 py-1 text-xs font-medium transition-colors ${
            token === null ? "bg-sr-lemon-muted text-sr-espresso" : "bg-sr-surface text-sr-neutral-300 hover:text-white"
          }`}
        >
          All
        </button>
        {tokens.map((t) => (
          <button
            key={t}
            onClick={() => setToken(t)}
            className={`sr-focus-ring rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              token === t ? "bg-sr-lemon-muted text-sr-espresso" : "bg-sr-surface text-sr-neutral-300 hover:text-white"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {groups.length === 0 ? (
        <div className="sr-panel px-5 py-12 text-center text-sm text-sr-neutral-500">
          No news has been published yet.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {groups.map((group) => (
            <div key={group.label}>
              <h2 className="mb-1 px-1 text-xs font-semibold uppercase tracking-wide text-sr-salmon">{group.label}</h2>
              <div className="sr-panel overflow-hidden">
                {group.items.map((item, i) => (
                  <NewsRow key={`${item.timeMs}-${i}`} item={item} index={linkIndex} onTradeMarket={onTradeMarket} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
